import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { catchError, distinctUntilChanged, EMPTY, filter, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ToastModule } from 'primeng/toast';
import { NgxSpinnerComponent } from 'ngx-spinner';
import { UserService } from '@/core/services/user.service';
import { UserApiModel } from '@/core/models/api/user-api-model.model';
import { ProfileType } from '@/types/msal';
import { MsalGraphService } from '@/core/services/msal-graph.service';
import { environment } from 'environments/environment';
import { AUTH_TOAST_KEY } from '@/core/interceptors/auth-error.interceptor';
import { AppAccessService, NO_ACCESS_ROUTE } from '@/core/services/app-access.service';
import { DraggableDialogService } from '@/core/services/draggable-dialog.service';
@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterModule, ToastModule, NgxSpinnerComponent],
    template: `<ngx-spinner bdColor = "rgba(0, 0, 0, 0.8)" size = "medium" color = "#fff" type = "ball-fussion" [fullScreen] = "true"></ngx-spinner>
    <p-toast></p-toast>
    <!-- Mensajes de autorización (401/403) del AuthErrorInterceptor. Van en un toast propio
         abajo-derecha porque muchas pantallas montan su propio <p-toast> arriba-derecha (con su
         propia instancia local de MessageService): en la misma esquina los dos contenedores se
         solapan y el mensaje útil queda tapado por el genérico de la pantalla. -->
    <p-toast [key]="AUTH_TOAST_KEY" position="bottom-right"></p-toast>
                <router-outlet></router-outlet>`
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly _destroying$ = new Subject<void>();
    loginDisplay = false;
    /** Clave del toast de autorización. Debe coincidir con la del AuthErrorInterceptor. */
    readonly AUTH_TOAST_KEY = AUTH_TOAST_KEY;
    constructor(private msalAuthService: MsalService,
        private msalGraphService: MsalGraphService,
        private userService: UserService,
        @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
        private msalBroadcastService: MsalBroadcastService,
        private router: Router,
        private appAccess: AppAccessService,
        // Sin uso directo: instanciarlo alcanza para que arranque a observar los diálogos.
        private draggableDialogService: DraggableDialogService
    ) {
    }

    ngOnInit(): void {
        this.initClarity();
        this.msalAuthService
            .handleRedirectObservable()
            .pipe(takeUntil(this._destroying$))
            .subscribe({
                next: () => {
                    try {
                        this.msalAuthService.instance.enableAccountStorageEvents();
                    } catch (e) {
                        console.warn('MSAL account storage events could not be enabled', e);
                    }
                },
                error: (err) => {
                    console.error('Error in handleRedirectObservable', err);
                }
            });
        // this.getToken();
        this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.ACCOUNT_ADDED ||
                        msg.eventType === EventType.ACCOUNT_REMOVED
                )
            )
            .subscribe((result: EventMessage) => {
                if (this.msalAuthService.instance.getAllAccounts().length === 0) {
                    window.location.pathname = '/';
                } else {
                    this.setLoginDisplay();
                }
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
                switchMap((result: EventMessage) => {
                    const payload = result.payload as AuthenticationResult;
                    this.msalAuthService.instance.setActiveAccount(payload.account);
                    return of(payload.account);
                }),
                switchMap(() => this.msalGraphService.getProfileInformation()),
                switchMap((profile: ProfileType) => {
                    if (!profile || !profile.mail) {
                        return EMPTY;
                    }
                    this.userService.profileTypeInfoValue = profile;
                    return this.userService.getByUserName(profile.mail).pipe(
                        switchMap(user => {
                            if (!user) {
                                const userApiModel = new UserApiModel(
                                    {
                                        userName: profile.mail || '',
                                        uidConcreto: null,
                                        officeLocation: profile.officeLocation || '',
                                        displayName: profile.displayName || '',
                                    }
                                );

                                return this.userService.add(userApiModel);
                            }
                            return of(user);
                        }),
                        catchError(error => {
                            console.error('Error in user processing:', error);
                            return of(null);
                        })
                    );
                }),
                takeUntil(this._destroying$)
            )
            .subscribe((user: UserApiModel | null) => {
                if (!user) {
                    // Una cuenta sin ningún rol 403ea también acá (`GET /User/...`), y este navigate
                    // pisaba la pantalla de bloqueo a la que ya la mandó `AppAccessService.expel()`:
                    // el usuario terminaba en un 404 que no explica nada. La expulsión manda.
                    if (this.appAccess.hasNoRoles()) {
                        return;
                    }
                    // No se pudo resolver el usuario de la app pese a haber iniciado sesión. Antes
                    // esto iba a `/not-found` — ruta inexistente, así que el comodín `**` remataba
                    // en `/notfound`: un 404 sobre una pantalla vacía, sin logout ni forma de
                    // cambiar de cuenta. `/no-access` al menos dice con qué cuenta se entró y deja
                    // salir o entrar con otra.
                    this.router.navigate([NO_ACCESS_ROUTE]);
                    return;
                }
            });
        // .subscribe((account: AccountInfo | null) => {
        //     // console.log('LOGIN_SUCCESS', result);
        //     // const payload = result.payload as AuthenticationResult;
        //     // this.msalAuthService.instance.setActiveAccount(payload.account);
        // });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None)
            )
            .subscribe(() => {
                this.setLoginDisplay();
            });
        // this.msalBroadcastService.msalSubject$
        // .pipe(
        //       filter(
        //             (msg: EventMessage) =>{
        //                 console.log('LOG EVENT MESSAGE: ', msg);
        //                 return msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS;
        //             }
        //         ),
        //     ).subscribe((result: EventMessage) => {
        //         const payload = result.payload as AuthenticationResult;
        //         console.log('Login SSO Silent Success payload', payload);
        //         this.msalAuthService.instance.setActiveAccount(payload.account);
        //     });
        // this.msalBroadcastService.msalSubject$
        //     .pipe(
        //         filter(
        //             (msg: EventMessage) =>{
        //                 return msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS;
        //             }
        //         ),
        //         map((event: EventMessage) => {
        //             const accounts = this.msalAuthService.instance.getAllAccounts();
        //             return accounts.length > 0 ? accounts[0].username : null;
        //         }),
        //         distinctUntilChanged(),
        //         filter(account => account !== null),
        //         switchMap(() => this.msalGraphService.getProfileInformation()),
        //         switchMap((profile: ProfileType) => {
        //             if (!profile || !profile.mail) {
        //                 return EMPTY;
        //             }
        //             this.userService.profileTypeInfoValue = profile;
        //             return this.userService.getByUserName(profile.mail).pipe(
        //                 switchMap(user => {
        //                     if (!user) {
        //                         const userApiModel = new UserApiModel(
        //                             {
        //                                 userName: profile.mail || '',
        //                                 uidConcreto: null,
        //                                 officeLocation: profile.officeLocation || '',
        //                                 displayName: profile.displayName || '',
        //                             }
        //                         );

        //                         return this.userService.add(userApiModel);
        //                     }
        //                     return of(user);
        //                 }),
        //                 catchError(error => {
        //                     console.error('Error in user processing:', error);
        //                     return of(null);
        //                 })
        //             );
        //         }),
        //         takeUntil(this._destroying$)
        //     )
        //     .subscribe((user: UserApiModel | null) => {
        //         if (!user) {
        //             this.router.navigate(['/not-found']);
        //             return;
        //         }
        //     });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter(
                    (status: InteractionStatus) => status === InteractionStatus.Login
                ),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                console.log('MSAL InteractionStatus is Login');
                // this.setLoginDisplay();
                // this.checkAndSetActiveAccount();
            });
    }
    checkAndSetActiveAccount() {
        /**
         * If no active account set but there are accounts signed in, sets first account to active account
         * To use active account set here, subscribe to inProgress$ first in your component
         * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
         */
        let activeAccount = this.msalAuthService.instance.getActiveAccount();

        if (
            !activeAccount &&
            this.msalAuthService.instance.getAllAccounts().length > 0
        ) {
            let accounts = this.msalAuthService.instance.getAllAccounts();
            this.msalAuthService.instance.setActiveAccount(accounts[0]);
        }
    }

    loginRedirect() {
        if (this.msalGuardConfig.authRequest) {
            this.msalAuthService.loginRedirect({
                ...this.msalGuardConfig.authRequest,
            } as RedirectRequest);
        } else {
            this.msalAuthService.loginRedirect();
        }
    }

    loginPopup() {
        if (this.msalGuardConfig.authRequest) {
            this.msalAuthService
                .loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
                .subscribe((response: AuthenticationResult) => {
                    this.msalAuthService.instance.setActiveAccount(response.account);
                });
        } else {
            this.msalAuthService
                .loginPopup()
                .subscribe((response: AuthenticationResult) => {
                    this.msalAuthService.instance.setActiveAccount(response.account);
                });
        }
    }

    logout(popup?: boolean) {
        if (popup) {
            this.msalAuthService.logoutPopup({
                mainWindowRedirectUri: '/',
            });
        } else {
            this.msalAuthService.logoutRedirect();
        }
    }

    setLoginDisplay() {
        this.loginDisplay = this.msalAuthService.instance.getAllAccounts().length > 0;
    }
    getToken() {
        const activeAccount = this.msalAuthService.instance.getActiveAccount();
        console.log('ACTIVE ACCOUNT: ', activeAccount);

        if (!activeAccount) {
            this.msalAuthService.loginPopup();
            return;
        }
        // this.msalAuthService.acquireTokenRedirect(
        //     {
        //         account: activeAccount!,
        //         scopes: environment.apiConfig.scopes
        //     }
        // ).subscribe({
        //     next: (result) => {
        //         console.log('Token acquired successfully:', result);
        //     },
        //     error: (error) => {
        //         console.error('Error acquiring token:', error);
        //     }
        // })
        // if (!activeAccount) {
        //     // If no account is active, you may need to log in first
        //     await this.msalAuthService.loginRedirect();
        //     return null;
        // }

        // try {
        //     // Try to acquire silently first
        //     const result: AuthenticationResult =
        //         await this.msalAuthService.acquireTokenSilent({
        //             account: activeAccount,
        //             scopes: ['api://your-api-client-id/.default'], // <-- replace with your scopes
        //         });

        //     return result.accessToken;
        // } catch (error) {
        //     console.warn('Silent token acquisition failed, falling back to interactive', error);

        //     // Fallback: popup or redirect
        //     const result: AuthenticationResult =
        //         await this.msalAuthService.acquireTokenPopup({
        //             scopes: ['api://your-api-client-id/.default'],
        //         });

        //     return result.accessToken;
        // }
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    private initClarity(): void {
        if (environment.telemetry.clarityEnabled && environment.telemetry.clarityProjectId) {
            (function (c: any, l: any, a: any, r: any, i: any, t?: any, y?: any) {
                c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) };
                t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
                y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
            })(window, document, "clarity", "script", environment.telemetry.clarityProjectId);
        }
    }
}
